import Papa from 'papaparse';

export type MmcOutcome = 'finished' | 'dns' | 'dnf';
export type MmcGroupOutcome = MmcOutcome | 'mixed';
export type MmcLanguage = 'nb' | 'en';

export interface MmcParticipantEntry {
  name: string;
  firstName: string;
  distance: string;
  distanceShortName: string;
  bib: string;
  country: string;
  outcome: MmcOutcome;
  sourceRow: number;
}

export interface MmcParticipant {
  email: string;
  name: string;
  firstName: string;
  recipientName: string;
  recipientFirstName: string;
  participantCount: number;
  participantSummary: string;
  distance: string;
  distanceShortName: string;
  bib: string;
  country: string;
  language: MmcLanguage;
  outcome: MmcGroupOutcome;
  entries: MmcParticipantEntry[];
  sourceRows: number[];
}

export interface RejectedMmcRow { row: number; reason: string; }
export interface ParsedMmcData {
  participants: MmcParticipant[];
  rejected: RejectedMmcRow[];
  headers: string[];
  duplicateCount: number;
}

const MERGE_FIELD = /{{\s*(firstName|name|recipientName|recipientFirstName|participantCount|participantSummary|distance|distanceShortName|bib|email|country|outcome)\s*}}/g;
const REQUIRED_HEADERS = ['Status', 'DistanceShortName', 'Distance', 'Bib', 'Name', 'Email', 'Country'];

const titleCaseName = (value: string): string => value.trim().toLocaleLowerCase('nb-NO').replace(
  /(^|[\s-])([a-zæøå])/gi,
  (_, prefix: string, letter: string) => prefix + letter.toLocaleUpperCase('nb-NO')
);
const normalize = (value: string): string => value.toLocaleLowerCase('nb-NO').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9æøå]/g, '');
const isEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
const outcomeOf = (value: string): MmcOutcome => {
  const status = value.trim().toLocaleUpperCase('nb-NO');
  if (status === 'DNS') return 'dns';
  if (status === 'DNF') return 'dnf';
  return 'finished'; // RaceTracker exports successful/blank status as code 17.
};

const levenshtein = (a: string, b: string): number => {
  const row = Array.from({ length: b.length + 1 }, (_, index) => index);
  for (let i = 1; i <= a.length; i += 1) {
    let previous = row[0];
    row[0] = i;
    for (let j = 1; j <= b.length; j += 1) {
      const old = row[j];
      row[j] = Math.min(row[j] + 1, row[j - 1] + 1, previous + (a[i - 1] === b[j - 1] ? 0 : 1));
      previous = old;
    }
  }
  return row[b.length];
};

const findRegistrant = (email: string, entries: MmcParticipantEntry[]): MmcParticipantEntry | undefined => {
  if (entries.length === 1) return entries[0];
  const local = email.split('@')[0];
  const localNormalized = normalize(local);
  const localTokens = local.split(/[._+-]+/).map(normalize).filter(Boolean);
  const candidates = entries.map((entry) => {
    const tokens = entry.name.split(/\s+/).map(normalize).filter(Boolean);
    const first = tokens[0] || '';
    const last = tokens[tokens.length - 1] || '';
    const tokenScore = localTokens.length >= 2
      ? levenshtein(localTokens[0], first) + levenshtein(localTokens[localTokens.length - 1], last)
      : Number.POSITIVE_INFINITY;
    return { entry, score: Math.min(levenshtein(localNormalized, normalize(entry.name)), tokenScore) };
  }).sort((a, b) => a.score - b.score);
  return candidates[0]?.score <= 2 && candidates[0].score < (candidates[1]?.score ?? Number.POSITIVE_INFINITY)
    ? candidates[0].entry : undefined;
};

const naturalJoin = (values: string[], language: MmcLanguage): string => {
  const unique = Array.from(new Set(values.filter(Boolean)));
  if (unique.length < 2) return unique[0] || '';
  const conjunction = language === 'nb' ? ' og ' : ' and ';
  return `${unique.slice(0, -1).join(', ')}${conjunction}${unique[unique.length - 1]}`;
};

const outcomeLabel = (outcome: MmcOutcome, language: MmcLanguage): string => ({
  nb: { finished: 'fullført', dns: 'ikke startet (DNS)', dnf: 'startet, men ikke fullført (DNF)' },
  en: { finished: 'finished', dns: 'did not start (DNS)', dnf: 'started, but did not finish (DNF)' },
}[language][outcome]);

const finalizeGroup = (email: string, entries: MmcParticipantEntry[]): MmcParticipant => {
  const language: MmcLanguage = entries.every((entry) => entry.country.toLocaleLowerCase('nb-NO') === 'norge') ? 'nb' : 'en';
  const outcomes = Array.from(new Set(entries.map((entry) => entry.outcome)));
  const outcome: MmcGroupOutcome = outcomes.length === 1 ? outcomes[0] : 'mixed';
  const registrant = findRegistrant(email, entries);
  const recipientFirstName = registrant?.firstName || '';
  return {
    email,
    name: naturalJoin(entries.map((entry) => entry.name), language),
    firstName: recipientFirstName || (language === 'nb' ? 'deltakere' : 'participants'),
    recipientName: registrant?.name || '',
    recipientFirstName,
    participantCount: entries.length,
    participantSummary: entries.map((entry) => `- ${entry.name} (${entry.distanceShortName}): ${outcomeLabel(entry.outcome, language)}`).join('\n'),
    distance: naturalJoin(entries.map((entry) => entry.distance), language),
    distanceShortName: naturalJoin(entries.map((entry) => entry.distanceShortName), language),
    bib: entries.map((entry) => entry.bib).filter(Boolean).join(', '),
    country: naturalJoin(entries.map((entry) => entry.country), language),
    language, outcome, entries,
    sourceRows: entries.map((entry) => entry.sourceRow),
  };
};

export const repairRaceTrackerCsv = (text: string): string => text.replace(/^\uFEFF/, '').split(/\r?\n/).map((line) => {
  if (line.startsWith('"') && line.endsWith('"')) return line.slice(1, -1).replace(/""/g, '"');
  return line;
}).join('\n');

export const parseMmcRows = (rawRows: unknown[][]): ParsedMmcData => {
  const rows = rawRows.map((row) => row.map((cell) => String(cell ?? '').trim()));
  if (rows.length < 2) throw new Error('Filen inneholder ingen deltakerrader.');
  const headers = rows[0].map((header) => header.replace(/^\uFEFF/, ''));
  const indexOf = (name: string): number => headers.indexOf(name);
  const missing = REQUIRED_HEADERS.filter((name) => indexOf(name) < 0);
  if (missing.length) throw new Error(`Mangler forventede kolonner: ${missing.join(', ')}`);

  const entriesByEmail = new Map<string, MmcParticipantEntry[]>();
  const rejected: RejectedMmcRow[] = [];
  let duplicateCount = 0;
  rows.slice(1).forEach((row, index) => {
    const sourceRow = index + 2;
    const value = (name: string): string => String(row[indexOf(name)] || '').trim();
    const email = value('Email').toLocaleLowerCase('nb-NO');
    if (!isEmail(email)) {
      rejected.push({ row: sourceRow, reason: email ? 'Ugyldig e-postadresse' : 'Mangler e-postadresse' });
      return;
    }
    const name = titleCaseName(value('Name'));
    const entry: MmcParticipantEntry = {
      name,
      firstName: name.split(/\s+/)[0] || (value('Country') === 'Norge' ? 'deltaker' : 'participant'),
      distance: value('Distance'), distanceShortName: value('DistanceShortName'), bib: value('Bib'),
      country: value('Country'), outcome: outcomeOf(value('Status')), sourceRow,
    };
    const existing = entriesByEmail.get(email);
    if (existing) { existing.push(entry); duplicateCount += 1; }
    else entriesByEmail.set(email, [entry]);
  });

  return {
    participants: Array.from(entriesByEmail, ([email, entries]) => finalizeGroup(email, entries)),
    rejected, headers, duplicateCount,
  };
};

export const parseMmcCsv = (text: string): ParsedMmcData => {
  const parsed = Papa.parse<string[]>(repairRaceTrackerCsv(text), { header: false, skipEmptyLines: 'greedy' });
  if (parsed.errors.length) throw new Error(parsed.errors[0].message);
  return parseMmcRows(parsed.data);
};

export const mergeMmcFields = (template: string, participant: MmcParticipant): string =>
  template.replace(MERGE_FIELD, (_, key: keyof MmcParticipant) => String(participant[key] ?? ''));

const addressee = (group: MmcParticipant): string => {
  if (group.recipientFirstName) return group.recipientFirstName;
  if (group.participantCount > 1) return group.language === 'nb' ? 'deltakere' : 'participants';
  return group.entries[0]?.firstName || (group.language === 'nb' ? 'deltaker' : 'participant');
};

export const getSharedDistanceShortName = (group: MmcParticipant): string => {
  const distances = Array.from(new Set(group.entries.map((entry) => entry.distanceShortName).filter(Boolean)));
  return distances.length === 1 ? distances[0] : '';
};

export const getMmcSubject = (group: MmcParticipant): string => {
  const name = group.recipientFirstName ? `, ${group.recipientFirstName}` : '';
  const sharedDistance = getSharedDistanceShortName(group);
  if (group.language === 'en') {
    if (group.outcome === 'dns') return sharedDistance
      ? `We missed you at the start of the ${sharedDistance} at MMC 2026${name}`
      : `We missed you at the start of MMC 2026${name}`;
    if (group.outcome === 'dnf' || group.outcome === 'finished') return sharedDistance
      ? `Thank you for your effort in the ${sharedDistance} at MMC 2026${name}!`
      : `Thank you for your effort at MMC 2026${name}!`;
    return `A message after MMC 2026${name}`;
  }
  if (group.outcome === 'dns') {
    const pronoun = group.participantCount === 1 ? 'deg' : 'dere';
    return sharedDistance
      ? `Vi savnet ${pronoun} på startstreken til ${sharedDistance} under MMC 2026${name}`
      : `Vi savnet ${pronoun} på startstreken under MMC 2026${name}`;
  }
  if (group.outcome === 'dnf' || group.outcome === 'finished') return sharedDistance
    ? `Takk for innsatsen på ${sharedDistance} under MMC 2026${name}!`
    : `Takk for innsatsen under MMC 2026${name}!`;
  return `En hilsen etter MMC 2026${name}`;
};

export const getMmcIntro = (group: MmcParticipant): string => {
  const greeting = group.language === 'nb' ? `Kjære ${addressee(group)},` : `Dear ${addressee(group)},`;
  const plural = group.participantCount > 1;
  const sharedDistance = getSharedDistanceShortName(group);
  if (group.language === 'en') {
    if (group.outcome === 'dns') return `${greeting}\n\nWe missed ${plural ? 'you all' : 'you'} at the start of MMC 2026 and hope everything is well. We would still like to share the results, photos and practical post-event information with you.`;
    if (group.outcome === 'dnf') return `${greeting}\n\n${sharedDistance
      ? `Thank you for starting the ${sharedDistance} at **Meråker Mountain Challenge 2026**.`
      : 'Thank you for making it to the start of **Meråker Mountain Challenge 2026**.'}`;
    if (group.outcome === 'mixed') return `${greeting}\n\nHere is one combined message for everyone registered with this email address. The participant records have different outcomes:\n\n${group.participantSummary}`;
    return `${greeting}\n\n${plural ? 'Thank you all for joining the MMC mountain festival last weekend!' : 'Thank you for taking part in **Meråker Mountain Challenge 2026**!'} We hope you brought home some great memories from a wonderful weekend in Meråker.`;
  }
  if (group.outcome === 'dns') return `${greeting}\n\nVi savnet ${plural ? 'dere' : 'deg'} på startstreken under MMC 2026 og håper alt står bra til. Vi vil likevel gjerne dele resultater, bilder og praktisk etterinformasjon fra arrangementet.`;
  if (group.outcome === 'dnf') return `${greeting}\n\nTakk for at ${plural ? 'dere' : 'du'} stilte til start${sharedDistance ? ` på ${sharedDistance}` : ''} under **Meråker Mountain Challenge 2026**.`;
  if (group.outcome === 'mixed') return `${greeting}\n\nHer kommer én samlet hilsen til alle som er registrert med denne e-postadressen. Deltakerregistreringene har ulike statuser:\n\n${group.participantSummary}`;
  return `${greeting}\n\n${plural ? 'Takk for at dere deltok på MMC-fjellfestivalen forrige helg!' : 'Tusen takk for at du var med på **Meråker Mountain Challenge 2026**!'} Vi håper ${plural ? 'dere' : 'du'} sitter igjen med gode opplevelser fra en flott helg i Meråker.`;
};

export const buildLocalizedMmcMarkup = (group: MmcParticipant, norwegianBody: string, englishBody: string): string =>
  `${getMmcIntro(group)}\n\n${mergeMmcFields(group.language === 'nb' ? norwegianBody : englishBody, group)}`.trim();

const escapeHtml = (value: string): string => value.replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }[char] || char));
const safeLink = (url: string): string => /^(https:\/\/|mailto:)/i.test(url.replace(/&amp;/g, '&')) ? url : '#';

export const simpleMarkupToHtml = (source: string): string => {
  const lines = escapeHtml(source).split(/\r?\n/);
  const blocks: string[] = [];
  let listItems: string[] = [];
  const inline = (value: string): string => value.replace(/\[([^\]]+)]\(([^)]+)\)/g, (_, label, url) => `<a href="${safeLink(url)}" style="color:#ED1C24;text-decoration:underline;">${label}</a>`).replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  const flushList = () => {
    if (!listItems.length) return;
    blocks.push(`<ul style="margin:0 0 18px;padding-left:22px;">${listItems.map((item) => `<li style="margin:0 0 7px;">${inline(item)}</li>`).join('')}</ul>`);
    listItems = [];
  };
  lines.forEach((rawLine) => {
    const line = rawLine.trim();
    if (!line) { flushList(); return; }
    if (line.startsWith('- ')) { listItems.push(line.slice(2)); return; }
    flushList();
    if (line.startsWith('## ')) blocks.push(`<h2 style="font-size:20px;line-height:1.3;color:#ED1C24;margin:28px 0 10px;">${inline(line.slice(3))}</h2>`);
    else blocks.push(`<p style="font-size:16px;line-height:1.65;color:#272727;margin:0 0 16px;">${inline(line)}</p>`);
  });
  flushList();
  return blocks.join('\n');
};

export const buildMmcEmailHtml = (markup: string, logoUrl: string, language: MmcLanguage = 'nb'): string => {
  const body = simpleMarkupToHtml(markup);
  return `<!doctype html><html lang="${language === 'nb' ? 'no' : 'en'}"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head><body style="margin:0;padding:0;background:#f3f1ed;font-family:Arial,Helvetica,sans-serif;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#f3f1ed;"><tr><td align="center" style="padding:24px 12px;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:660px;background:#fff;border-radius:10px;overflow:hidden;"><tr><td align="center" style="padding:24px 28px 8px;"><img src="${escapeHtml(logoUrl)}" alt="Meråker Mountain Challenge" width="552" style="display:block;width:100%;max-width:552px;height:auto;border:0;"></td></tr><tr><td style="padding:18px 38px 32px;">${body}</td></tr><tr><td style="background:#ED1C24;color:#fff;text-align:center;padding:18px 28px;font-size:13px;line-height:1.7;">Meråker Mountain Challenge · <a href="mailto:post@mmctrail.no" style="color:#fff;">post@mmctrail.no</a><br><a href="${language === 'nb' ? 'https://mmctrail.no/nb' : 'https://mmctrail.no'}" style="color:#fff;text-decoration:underline;">${language === 'nb' ? 'mmctrail.no/nb' : 'mmctrail.no'}</a><!-- MMC_UNSUBSCRIBE_FOOTER --></td></tr></table></td></tr></table></body></html>`;
};

export const markupToPlainText = (markup: string, language: MmcLanguage = 'nb'): string =>
  `${markup.trim()}\n\n---\nMeråker Mountain Challenge\npost@mmctrail.no\n${language === 'nb' ? 'https://mmctrail.no/nb' : 'https://mmctrail.no'}`;
export const containsMarketingSections = (value: string): boolean => /Påmelding til MMC 2027|Registration for MMC 2027|25\s*% rabatt|25\s*% discount|226ERS/i.test(value);
export const removeMarketingSections = (value: string): string => value
  .replace(/\n?## Påmelding til MMC 2027[\s\S]*?(?=\n## |$)/i, '\n')
  .replace(/\n?## Registration for MMC 2027[\s\S]*?(?=\n## |$)/i, '\n')
  .replace(/\n?## 25\s*% rabatt hos 226ERS[\s\S]*?(?=\n## |$)/i, '\n')
  .replace(/\n?## 25\s*% discount at 226ERS[\s\S]*?(?=\n## |$)/i, '\n')
  .replace(/\n{3,}/g, '\n\n').trim();
